import { useCallback, useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Play, Wrench as WrenchIcon, FileText, ExternalLink } from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import { formatSlotShort } from "@/lib/slot";
import PageHeader from "@/components/PageHeader";
import JobStatusBadge, { JobPriorityBadge } from "@/components/JobStatusBadge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import ExecuteTab from "@/components/engineer/ExecuteTab";

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "machines", label: "Machines & History" },
  { key: "execute", label: "Execute" },
];

export default function EngineerJobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/jobs/${id}`);
      setJob(data);
      setError(null);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    if (job?.status === "in_progress" && tab === "overview") setTab("execute");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.status]);

  if (loading) return <div className="p-6 text-sm text-muted-foreground" data-testid="engineer-detail-loading">Loading…</div>;
  if (error) return (
    <div className="p-6" data-testid="engineer-detail-error">
      <p className="text-sm text-destructive mb-3">{error}</p>
      <Button variant="outline" onClick={() => navigate("/my/jobs")}>Back to My Jobs</Button>
    </div>
  );
  if (!job) return null;

  const executeAllowed = job.status === "in_progress";
  const isTerminal = ["completed", "closed"].includes(job.status);

  const startJob = async () => {
    try {
      const { data } = await api.post(`/jobs/${job.id}/start`);
      setJob(data);
      setTab("execute");
      toast.success("Job started");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="p-4 sm:p-6 space-y-4 max-w-6xl mx-auto pb-32" data-testid="engineer-detail-page">
      <div className="flex items-center gap-2">
        <Link to="/my/jobs" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid="engineer-detail-back">
          <ArrowLeft className="h-3.5 w-3.5" /> My Jobs
        </Link>
      </div>
      <PageHeader
        testId="engineer-detail-header"
        title={<span className="flex items-center gap-2"><span className="font-mono text-base">{job.job_number}</span><span className="truncate">{job.title}</span></span>}
        subtitle={`${job.customer_name} · ${job.site_name}`}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <JobStatusBadge status={job.status} />
            <JobPriorityBadge priority={job.priority} />
            {job.status === "scheduled" && (
              <Button size="lg" className="min-h-11 gap-2" onClick={startJob} data-testid="engineer-start-job">
                <Play className="h-4 w-4" /> Start Job
              </Button>
            )}
          </div>
        }
      />

      <div className="rounded-lg bg-muted p-1 flex gap-1" role="tablist" data-testid="engineer-detail-tabs">
        {TABS.map((t) => {
          const disabled = t.key === "execute" && !executeAllowed && !isTerminal;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              onClick={() => !disabled && setTab(t.key)}
              disabled={disabled}
              data-testid={`engineer-detail-tab-${t.key}`}
              className={`flex-1 min-h-11 px-3 text-sm rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${tab === t.key ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === "overview" && <OverviewTab job={job} onStart={startJob} />}
      {tab === "machines" && <MachinesTab job={job} />}
      {tab === "execute" && (isTerminal
        ? <ExecuteReadOnly job={job} />
        : executeAllowed
          ? <ExecuteTab job={job} onChange={reload} />
          : <Card className="rounded-md"><CardContent className="p-6 text-sm text-muted-foreground" data-testid="engineer-execute-locked">Start the job first to open the execution flow.</CardContent></Card>
      )}
    </div>
  );
}

function OverviewTab({ job, onStart }) {
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card className="rounded-md">
        <CardHeader><CardTitle className="text-sm font-medium flex items-center gap-2"><FileText className="h-4 w-4 text-primary" />Description</CardTitle></CardHeader>
        <CardContent className="text-sm whitespace-pre-wrap leading-relaxed" data-testid="engineer-overview-description">
          {job.description || <span className="text-muted-foreground italic">No description.</span>}
        </CardContent>
      </Card>
      <Card className="rounded-md">
        <CardHeader><CardTitle className="text-sm font-medium">Site access notes</CardTitle></CardHeader>
        <CardContent className="text-sm whitespace-pre-wrap leading-relaxed" data-testid="engineer-overview-site-access">
          {job.site_access_notes_snapshot || <span className="text-muted-foreground italic">No access notes captured.</span>}
        </CardContent>
      </Card>
      <Card className="rounded-md lg:col-span-2">
        <CardHeader><CardTitle className="text-sm font-medium">Schedule</CardTitle></CardHeader>
        <CardContent className="space-y-1.5" data-testid="engineer-overview-schedule">
          {(job.scheduled_slots || []).length === 0 ? (
            <div className="text-sm text-muted-foreground italic">No slots.</div>
          ) : (job.scheduled_slots || []).map((s, i) => (
            <div key={i} className="text-sm flex items-center gap-2" data-testid={`engineer-overview-slot-${i}`}>
              <span className="font-mono text-xs">{s.date}</span>
              <span className="text-muted-foreground">·</span>
              <span>{formatSlotShort(s)}</span>
              {s.notes && <span className="text-muted-foreground text-xs italic">— {s.notes}</span>}
            </div>
          ))}
        </CardContent>
      </Card>
      {job.status === "scheduled" && (
        <div className="lg:col-span-2">
          <Button size="lg" className="w-full min-h-14 text-base gap-2" onClick={onStart} data-testid="engineer-overview-start">
            <Play className="h-5 w-5" /> Start Job
          </Button>
        </div>
      )}
    </div>
  );
}

function MachinesTab({ job }) {
  const [manuals, setManuals] = useState({});
  useEffect(() => {
    (job.machines || []).forEach(async (m) => {
      try {
        const { data } = await api.get(`/machines/${m.id}/manuals`);
        setManuals((prev) => ({ ...prev, [m.id]: data }));
      } catch { /* silent */ }
    });
  }, [job.machines]);

  return (
    <div className="space-y-4" data-testid="engineer-machines-tab">
      {(job.machines || []).map((m) => (
        <Card key={m.id} className="rounded-md" data-testid={`engineer-machine-card-${m.id}`}>
          <CardHeader>
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <WrenchIcon className="h-4 w-4 text-primary" />
              {m.make} {m.model} <span className="text-xs text-muted-foreground font-normal">· S/N {m.serial}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-3">
            <div><span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Type</span> · {m.machine_type?.name || "—"}</div>
            {m.install_date && <div><span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Installed</span> · {m.install_date}</div>}
            {m.notes && <div className="text-muted-foreground italic">{m.notes}</div>}
            {(manuals[m.id] || []).length > 0 && (
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1">Manuals</div>
                <ul className="space-y-1">
                  {(manuals[m.id] || []).map((f) => (
                    <li key={f.id}>
                      <a
                        href={f.url}
                        target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline"
                        data-testid={`engineer-manual-${m.id}-${f.id}`}
                      >
                        <FileText className="h-3.5 w-3.5" /> {f.filename} <ExternalLink className="h-3 w-3" />
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
      {(job.previous_services || []).length > 0 && (
        <Card className="rounded-md">
          <CardHeader><CardTitle className="text-sm font-medium">Previous services (this machine set)</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-1.5" data-testid="engineer-previous-services">
            {(job.previous_services || []).map((p) => (
              <div key={p.id} className="flex items-center gap-2">
                <span className="font-mono text-xs">{p.job_number}</span>
                <JobStatusBadge status={p.status} />
                {p.completed_at && <span className="text-xs text-muted-foreground">completed {new Date(p.completed_at).toLocaleDateString()}</span>}
                {p.engineer?.full_name && <span className="text-xs text-muted-foreground">by {p.engineer.full_name}</span>}
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function ExecuteReadOnly({ job }) {
  const exe = job.execution;
  if (!exe) return <Card className="rounded-md"><CardContent className="p-6 text-sm text-muted-foreground">No execution data recorded.</CardContent></Card>;
  return (
    <Card className="rounded-md" data-testid="engineer-execute-readonly">
      <CardHeader><CardTitle className="text-sm font-medium">Submitted execution</CardTitle></CardHeader>
      <CardContent className="text-sm space-y-3">
        {exe.started_at && <div><span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Started</span> · {new Date(exe.started_at).toLocaleString()}</div>}
        {exe.left_at && <div><span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Left</span> · {new Date(exe.left_at).toLocaleString()}</div>}
        <div><span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Time entries</span> · {(exe.time_entries || []).length}</div>
        <div><span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Photos</span> · {(exe.photos || []).length}</div>
        <div><span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Machines reported</span> · {(exe.machines_report || []).length}</div>
        {job.actions_required && (
          <div className="bg-amber-500/10 border border-amber-500/40 rounded-sm p-3">
            <div className="text-xs font-bold uppercase tracking-wider text-amber-700 dark:text-amber-400 mb-1">Actions required</div>
            <div className="text-sm">{exe.actions_required_notes || "—"}</div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Engineer signature</div>
            <div>{exe.signatures?.engineer?.name || "—"}</div>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Customer signature</div>
            <div>
              {exe.signatures?.customer?.refused
                ? <span className="text-amber-700 dark:text-amber-400">Refused — {exe.signatures.customer.reason}</span>
                : exe.signatures?.customer?.name || "—"}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
